/**
 * Tartı etiketindeki ağırlık barkodunun çözümlenmesi.
 *
 * CAS CL3000 üzerinde doğrulanan format (EAN-13):
 *
 *   2 7 | 0 1 0 0 1 | 0 1 9 6 0 | 9
 *   └prefix┘ └─ kod ─┘ └─ gram ─┘ └kontrol
 *      2        5          5        1
 *
 * Fiyat barkoda gömülmüyor, ağırlık gömülüyor. Tutarı kasa hesaplar; böylece
 * fiyat değişse bile basılmış etiket geçerli kalır.
 *
 * Ağırlık gram cinsinden 5 hane → 99.999 kg'a kadar. CL3000 15 kg kapasiteli.
 */

import { ean13CheckDigit } from '../../../../utils/ean13';

/** Ağırlık barkodu tek uzunlukta: EAN-13. */
const BARCODE_LENGTH = 13;
const PREFIX_LENGTH = 2;
const CODE_LENGTH = 5;
const WEIGHT_LENGTH = 5;

/** Tartı tanımlı değilken varsayılan prefix. Cihaz fabrika ayarı 27. */
export const DEFAULT_WEIGHT_BARCODE_PREFIX = '27';

export interface WeightBarcode {
  /** Barkodun ilk iki hanesi */
  prefix: string;
  /** Ürünün tartı kodu — beş hane, baştaki sıfırlar korunur */
  scaleCode: string;
  /** Etikete basılan ağırlık, kilogram */
  weightKg: number;
  /** Aynı ağırlık gram cinsinden — yuvarlama tartışması çıkmasın diye ham hali */
  weightGrams: number;
}

/**
 * Okunan barkodu ağırlık barkodu olarak çözmeye çalışır.
 *
 * Çözemezse `null` döner ve çağıran taraf barkodu normal ürün araması olarak
 * sürdürür — bu yüzden burada asla exception atılmaz.
 *
 * @param raw Okuyucudan gelen ham metin
 * @param prefixes Kabul edilen prefix listesi. Boş verilirse varsayılan kullanılır.
 */
export function parseWeightBarcode(
  raw: string | null | undefined,
  prefixes: string[] = [DEFAULT_WEIGHT_BARCODE_PREFIX]
): WeightBarcode | null {
  if (!raw) return null;

  const digits = raw.replace(/\D/g, '');
  if (digits.length !== BARCODE_LENGTH) return null;

  const prefix = digits.slice(0, PREFIX_LENGTH);
  const accepted = (prefixes.length ? prefixes : [DEFAULT_WEIGHT_BARCODE_PREFIX])
    .map((p) => (p || '').replace(/\D/g, ''))
    .filter((p) => p.length === PREFIX_LENGTH);

  if (!accepted.includes(prefix)) return null;

  // Kontrol hanesi tutmuyorsa bu bir tartı etiketi değil: prefix'i tesadüfen
  // eşleşen bozuk/farklı bir barkodu ağırlıkmış gibi sepete atmayalım
  if (ean13CheckDigit(digits.slice(0, 12)) !== digits.charCodeAt(12) - 48) return null;

  const scaleCode = digits.slice(PREFIX_LENGTH, PREFIX_LENGTH + CODE_LENGTH);
  const weightGrams = Number(digits.slice(PREFIX_LENGTH + CODE_LENGTH, PREFIX_LENGTH + CODE_LENGTH + WEIGHT_LENGTH));

  // Sıfır gramlık etiket anlamsız; sepete 0 kg satır düşmesin
  if (!weightGrams) return null;

  return {
    prefix,
    scaleCode,
    weightKg: weightGrams / 1000,
    weightGrams,
  };
}

/**
 * Ağırlık barkodu üretir. Etiket tasarımcısı önizlemesi ve testler için;
 * mağazada etiketi tartının kendisi basıyor.
 */
export function buildWeightBarcode(
  scaleCode: string,
  weightGrams: number,
  prefix: string = DEFAULT_WEIGHT_BARCODE_PREFIX
): string {
  const p = (prefix || '').replace(/\D/g, '').padStart(PREFIX_LENGTH, '0').slice(-PREFIX_LENGTH);
  const code = (scaleCode || '').replace(/\D/g, '').padStart(CODE_LENGTH, '0').slice(-CODE_LENGTH);
  const grams = String(Math.max(0, Math.round(weightGrams))).padStart(WEIGHT_LENGTH, '0').slice(-WEIGHT_LENGTH);
  const body = `${p}${code}${grams}`;
  return `${body}${ean13CheckDigit(body)}`;
}

/**
 * Tartı kodunu beş haneye uyarlar. Kullanıcı ürün kartına "1" yazarsa
 * barkodda "00001" olarak görüneceği için burada da öyle saklanır.
 */
export function normalizeScaleCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let digits = String(raw).replace(/\D/g, '');
  if (!digits) return null;
  if (digits.length > CODE_LENGTH) digits = digits.slice(-CODE_LENGTH);
  return digits.padStart(CODE_LENGTH, '0');
}
